import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { CuadreUnit } from "../../../features/sg/cuadre";
import { StatusBadge } from "../components/cells";
import { BillingPill, Logos, ReportCell } from "./ui";

interface Props {
  rows: CuadreUnit[];
  onAccount: (id: number) => void;
  isDarkMode: boolean;
}

export default function UnitsTable({ rows, onAccount, isDarkMode }: Props) {
  const { t } = useTranslation("sg");
  const head = isDarkMode ? "text-gray-400 border-dark-border" : "text-gray-500 border-gray-200";
  const row = isDarkMode ? "border-dark-border hover:bg-white/[0.03]" : "border-gray-100 hover:bg-gray-50";
  const strong = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-500" : "text-gray-400";

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[880px] text-sm">
        <thead>
          <tr className={`border-b text-left text-[11px] uppercase tracking-wider ${head}`}>
            <th className="py-2.5 pr-4 font-medium">{t("cuadre.cols.unit")}</th>
            <th className="py-2.5 pr-4 font-medium">{t("cuadre.cols.account")}</th>
            <th className="py-2.5 pr-4 font-medium">{t("cuadre.cols.billing")}</th>
            <th className="py-2.5 pr-4 font-medium">{t("cuadre.cols.tag")}</th>
            <th className="py-2.5 pr-4 font-medium">{t("cuadre.cols.platforms")}</th>
            <th className="py-2.5 pr-4 font-medium">{t("cuadre.cols.lastReport")}</th>
            <th className="py-2.5 w-10" aria-label={t("cuadre.cols.actions")} />
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id} className={`border-b transition-colors ${row}`}>
              <td className="py-2.5 pr-4 max-w-[240px]">
                <div className={`truncate font-medium ${strong}`} title={u.name}>
                  {u.name || "—"}
                </div>
                <div className={`font-mono text-xs ${muted}`}>
                  {u.imei || "—"}
                  {u.plates && <span className="font-sans"> · {u.plates}</span>}
                </div>
                {(u.dupImei || u.tagMismatch) && (
                  <div className="text-[11px] text-amber-500 flex items-center gap-1">
                    <i className="ri-error-warning-line" aria-hidden="true" />
                    {[u.dupImei && t("cuadre.check.dupImei"), u.tagMismatch && t("cuadre.check.tagMismatch")].filter(Boolean).join(" · ")}
                  </div>
                )}
              </td>
              <td className="py-2.5 pr-4 max-w-[220px]">
                {u.accountId != null ? (
                  <button
                    type="button"
                    onClick={() => onAccount(u.accountId!)}
                    className={`max-w-full truncate text-left hover:underline ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                    title={t("cuadre.actions.filterAccount", { name: u.accountName })}
                  >
                    {u.accountName}
                  </button>
                ) : (
                  <span className={muted}>{t("cuadre.noAccount")}</span>
                )}
                {u.accountMismatch && (
                  <div className="text-[11px] text-amber-500 flex items-center gap-1">
                    <i className="ri-error-warning-line" aria-hidden="true" />
                    {t("cuadre.mismatchShort")}
                  </div>
                )}
              </td>
              <td className="py-2.5 pr-4">
                <BillingPill billing={u.billing} isDarkMode={isDarkMode} />
              </td>
              <td className="py-2.5 pr-4">
                {u.condition ? <StatusBadge label={u.condition} isDarkMode={isDarkMode} /> : <span className={muted}>—</span>}
              </td>
              <td className="py-2.5 pr-4">
                <Logos list={u.platforms} empty={t("cuadre.platform.none")} />
              </td>
              <td className={`py-2.5 pr-4 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                <ReportCell report={u.report} ts={u.lastReport} />
              </td>
              <td className="py-2.5 text-right">
                <Link
                  to={`/sg/devices/edit/${u.id}`}
                  title={t("cuadre.actions.editDevice")}
                  aria-label={t("cuadre.actions.editDevice")}
                  className={`inline-flex w-8 h-8 items-center justify-center rounded-lg transition-colors ${
                    isDarkMode ? "text-gray-400 hover:bg-white/10 hover:text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  <i className="ri-pencil-line" />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
